import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { RowItem } from '../../../stores/TimetableVersion/PinboardStoreTypes';
import { TimetableGridRow } from '../GridRow/TimetableGridRow';
import styles from './../TimetableGrid.module.scss';
import { useTimetableStore } from '../TimetableProvider';

type GridRowsProps = {
  daysWidth: string;
  cellWidth: string;
};

export const GridRows = observer(({ daysWidth, cellWidth }: GridRowsProps) => {
  const pinboardStore = useTimetableStore();
  const { t } = useTranslation();

  const rows = pinboardStore.getRows();
  const isFullscreen = pinboardStore.isFullscreen;

  const classes = classNames(styles.board, {
    [styles['is-fullscreen']]: isFullscreen,
  });

  // Beim Gruppieren nach Gebäuden bekommt jede Gebäude-Gruppe eine eigene Überschriftszeile.
  // Die Zeile ist rein visuell und liegt bewusst NICHT in `getRows()`, damit Karten-,
  // Auswahl- und Ausblend-Logik unberührt bleiben.
  const groupByBuilding = pinboardStore.groupRoomsByBuilding && pinboardStore.context === 'rooms';

  const rowsByBuilding = new Map<string, RowItem[]>();
  rows.forEach((row) => {
    const building = row.subLabel?.trim() ?? '';
    rowsByBuilding.set(building, [...(rowsByBuilding.get(building) ?? []), row]);
  });

  return (
    <div className={classes}>
      {rows.map((row, index) => {
        const building = row.subLabel?.trim() ?? '';
        const showBuildingRow = groupByBuilding && building !== (rows[index - 1]?.subLabel?.trim() ?? '');
        const buildingGroup = rowsByBuilding.get(building) ?? [];
        // Ist das ganze Gebäude ausgeblendet, bleibt nur seine Überschrift stehen —
        // ein Klick darauf blendet die Gruppe wieder ein.
        const collapsed = groupByBuilding && buildingGroup.every((r) => pinboardStore.hiddenRows.has(r.value));
        const classes = classNames(styles['grid-row'], {
          [styles['is-fullscreen']]: isFullscreen,
          [styles['building-row']]: showBuildingRow,
          [styles.collapsed]: collapsed,
        });
        return (
          <React.Fragment key={row.value}>
            {showBuildingRow && (
              <div
                className={classes}
                title={collapsed ? t('pinboard.actions.showRows') : t('pinboard.actions.hideRows')}
                onClick={() => pinboardStore.toggleRowsVisibility(buildingGroup)}
              >
                {building || t('common.noBuilding')}
              </div>
            )}
            {!collapsed && <TimetableGridRow row={row} cellWidth={cellWidth} daysWidth={daysWidth} />}
          </React.Fragment>
        );
      })}
    </div>
  );
});
